import {
  App,
  Button,
  Collapse,
  DatePicker,
  Form,
  Input,
  Select,
  Switch,
  type UploadFile,
} from 'antd';
import dayjs from 'dayjs';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import {
  HeaderType,
  ReportHeader,
} from 'business/quality-control/pages/defect-report-history/components/defect-report-details-drawer/components/report-header';
import { DefectReportStatus } from 'business/quality-control/pages/defect-report-history/hooks/data/types';
import { useBoardContext } from 'business/quality-control/pages/record-segment-defect/context/board-context';
import { db, DefectReport, RepairReport } from 'technical/db';
import { defectReportPictureSyncService } from 'technical/db/sync/defect-report-picture-sync.service';
import { repairReportPictureSyncService } from 'technical/db/sync/repair-report-picture-sync.service';
import { segmentSyncService } from 'technical/db/sync/segment-sync.service';
import {
  picturesBase64ToFiles,
  PictureUploadInput,
} from 'technical/picture-upload-input';
import {
  FULL_LOCALIZED_DATE_AND_TIME_FORMAT,
  formatUserName,
} from 'technical/string/formatter';

import { DefectFormButtons } from './buttons';
import styles from './index.module.scss';
import { DefectFormValues } from './types';

const DEFAULT_DRAW_COLOR = '#545454';

export const DefectForm = ({
  defectReportId,
  defectReport,
  ringId,
  isRepairEditable,
  repairReport,
  onDelete,
  closeDefect,
}: {
  defectReportId: string;
  defectReport: DefectReport | null;
  ringId: string;
  isRepairEditable: boolean;
  repairReport: RepairReport | null;
  onDelete: () => void;
  closeDefect: () => void;
}) => {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const [form] = Form.useForm<DefectFormValues>();
  const { message } = App.useApp();
  const { segmentKey, defectStroke, startDrawing, stopDrawing } =
    useBoardContext();
  const { extraQualityPermissions } = usePermissions();

  const initialized = useRef(false);
  const [activeKeys, setActiveKeys] = useState<string[]>(
    isRepairEditable ? [] : ['1'],
  );
  const [drawColor, setDrawColor] = useState(DEFAULT_DRAW_COLOR);

  const segmentType = Form.useWatch('segmentType', form);
  const defectReportTypeId = Form.useWatch('defectReportTypeId', form);
  const date = Form.useWatch('date', form);

  const isSubmitDisabled = !segmentType || !defectReportTypeId || !date;
  const isSubmitted = defectReport?.submittedAt != null;
  const isFormInputEditable =
    !isSubmitted && extraQualityPermissions.canEditReport;

  const defectReportTypes = useLiveQuery(
    () => db.defectReportTypes.toArray(),
    [],
  );

  const defectIndicators = useLiveQuery(
    () => db.defectIndicators.toArray(),
    [],
  );

  const rawPictures = useLiveQuery(
    () =>
      db.defectReportPictures
        .where('defectReportId')
        .equals(defectReportId)
        .toArray(),
    [],
  );

  useEffect(() => {
    if (!initialized.current && defectReport) {
      initialized.current = true;
      form.setFieldsValue({
        segmentType: defectReport.segmentType,
      });
    }
  }, [defectReport, form]);

  useEffect(() => {
    if (rawPictures !== undefined) {
      form.setFieldsValue({ pictures: picturesBase64ToFiles(rawPictures) });
    }
  }, [rawPictures, form]);

  useEffect(() => {
    if (!segmentKey) {
      return;
    }
    const type = segmentKey.split('#%id%#')[1];
    if (type) {
      form.setFieldValue('segmentType', type);
    }
  }, [segmentKey, form]);

  useEffect(() => {
    if (!isRepairEditable) {
      setActiveKeys(['1']);
    }
  }, [isRepairEditable]);

  const typeOptions = (defectReportTypes ?? []).map((type) => ({
    label: type.label,
    value: type.id,
  }));

  const indicatorOptions = (defectIndicators ?? []).map((indicator) => ({
    label: indicator.label,
    value: indicator.id,
  }));

  const handleDelete = async () => {
    try {
      // remove local pictures associated to the defect
      await defectReportPictureSyncService.deleteByDefect(defectReportId);

      await db.defectReports.update(defectReportId, {
        deletedAt: dayjs().toISOString(),
        desynchronizedAt: dayjs().toISOString(),
      });

      onDelete();
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.defectform.error',
        ),
      });
    }
  };

  const handleRemovePicture = async (file: UploadFile): Promise<boolean> => {
    try {
      await defectReportPictureSyncService.markForDeletion(file.uid);
      return true;
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.defectform.pictureDeleteError',
        ),
      });
      return false;
    }
  };

  const save = async (row: DefectFormValues, isSubmit: boolean) => {
    try {
      const strokesToSave =
        defectStroke?.normalizedPoints ?? defectReport?.strokes ?? [];
      if (!strokesToSave || strokesToSave.length <= 0) {
        form.setFields([
          {
            name: 'defectReportTypeId',
            errors: [
              t(
                `qualityControl.recordSegmentDefect.rightPanel.defectform.drawMissing`,
              ),
            ],
          },
        ]);
        return;
      }

      const segmentAssociated = await segmentSyncService.getByRingAndType(
        ringId,
        row.segmentType,
      );

      const baseFields = defectReport
        ? { ...defectReport }
        : {
            id: defectReportId,
            ringId,
            authorId: user?.id ?? '',
            author: formatUserName({
              firstName: user?.firstName,
              lastName: user?.lastName,
            }),
            strokes: strokesToSave,
          };

      await db.defectReports.put({
        ...baseFields,
        segmentType: row.segmentType,
        serialNumber: segmentAssociated ? segmentAssociated.serialNumber : null,
        defectReportTypeId: row.defectReportTypeId,
        defectIndicatorId: row.defectIndicatorId || null,
        description: row.description || null,
        toRepair: row.toRepair,
        createdAt:
          row.date?.toISOString() ??
          defectReport?.createdAt ??
          dayjs().toISOString(),
        submittedAt: isSubmit ? dayjs().toISOString() : null,
        desynchronizedAt: dayjs().toISOString(),
        deletedAt: null,
      });

      if (row.pictures.length > 0) {
        await defectReportPictureSyncService.createBulk(
          defectReportId,
          row.pictures,
        );
      }

      message.open({
        type: 'success',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.defectform.success',
        ),
      });

      stopDrawing();
      closeDefect();
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.defectform.error',
        ),
      });
    }
  };

  const handleInvalidate = async () => {
    try {
      if (repairReport) {
        await db.repairReports.update(repairReport.id, {
          deletedAt: dayjs().toISOString(),
          desynchronizedAt: dayjs().toISOString(),
        });
        await repairReportPictureSyncService.deleteByRepair(repairReport.id);
      }

      await db.defectReports.update(defectReportId, {
        submittedAt: null,
        desynchronizedAt: dayjs().toISOString(),
      });
      message.open({
        type: 'success',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.defectform.invalidateSuccess',
        ),
      });
    } catch (error) {
      message.open({
        type: 'error',
        content: t(
          'qualityControl.recordSegmentDefect.rightPanel.defectform.invalidateError',
        ),
      });
    }
  };

  const initialValues: DefectFormValues = {
    segmentType: defectReport?.segmentType ?? '',
    defectReportTypeId: defectReport?.defectReportTypeId ?? '',
    date: defectReport ? dayjs(defectReport.createdAt).tz() : dayjs().tz(),
    toRepair: defectReport?.toRepair ?? true,
    defectIndicatorId: defectReport?.defectIndicatorId ?? '',
    description: defectReport?.description ?? '',
    pictures: [],
  };

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
      expandIconPosition="end"
      style={{ background: '#fff' }}
      items={[
        {
          key: '1',
          label: (
            <ReportHeader
              status={
                defectReport?.submittedAt != null
                  ? DefectReportStatus.ReportSubmitted
                  : DefectReportStatus.ReportPending
              }
              type={HeaderType.defect}
            />
          ),
          children: (
            <>
              <Form<DefectFormValues>
                form={form}
                layout="vertical"
                onFinish={(row) => save(row, true)}
                initialValues={initialValues}
              >
                <Form.Item<DefectFormValues>
                  name="defectReportTypeId"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.type',
                  )}
                  rules={[{ required: true }]}
                >
                  <Select
                    options={typeOptions}
                    allowClear
                    disabled={!isFormInputEditable}
                    loading={defectReportTypes === undefined}
                    onChange={(value) => {
                      if (!value) {
                        return;
                      }
                      const defectType = defectReportTypes?.find(
                        (dt) => dt.id === value,
                      );
                      if (defectType) {
                        setDrawColor(defectType.color);
                      }
                    }}
                  />
                </Form.Item>

                {!defectReport?.createdAt && (
                  <div className={styles.drawButton}>
                    <Button
                      onClick={() => startDrawing(drawColor)}
                      type="dashed"
                      style={{ backgroundColor: '#00000011' }}
                    >
                      {t(
                        'qualityControl.recordSegmentDefect.rightPanel.defectform.cta.draw',
                      )}
                    </Button>
                  </div>
                )}

                <Form.Item<DefectFormValues>
                  name="segmentType"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.segmentType',
                  )}
                  rules={[{ required: true }]}
                  style={{ flex: 1 }}
                >
                  <Input disabled />
                </Form.Item>

                <Form.Item<DefectFormValues>
                  name="date"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.date',
                  )}
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    disabled={!isFormInputEditable}
                    showTime={{ format: 'LT' }}
                    format={FULL_LOCALIZED_DATE_AND_TIME_FORMAT}
                    inputReadOnly
                  />
                </Form.Item>

                <Form.Item<DefectFormValues>
                  name="toRepair"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.toRepair',
                  )}
                  valuePropName="checked"
                >
                  <Switch disabled={!isFormInputEditable} />
                </Form.Item>

                {indicatorOptions.length > 0 && (
                  <Form.Item<DefectFormValues>
                    name="defectIndicatorId"
                    label={t(
                      'qualityControl.recordSegmentDefect.rightPanel.defectform.indicator',
                    )}
                  >
                    <Select
                      options={indicatorOptions}
                      allowClear
                      disabled={!isFormInputEditable}
                    />
                  </Form.Item>
                )}

                <Form.Item<DefectFormValues>
                  name="description"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.description',
                  )}
                >
                  <Input.TextArea rows={3} disabled={!isFormInputEditable} />
                </Form.Item>

                <Form.Item<DefectFormValues>
                  name="pictures"
                  label={t(
                    'qualityControl.recordSegmentDefect.rightPanel.defectform.pictures',
                  )}
                >
                  <PictureUploadInput
                    maxCount={3}
                    disabled={!isFormInputEditable}
                    showRemoveIcon={isFormInputEditable}
                    onRemove={handleRemovePicture}
                  />
                </Form.Item>
              </Form>

              <DefectFormButtons
                form={form}
                isSubmitDisabled={isSubmitDisabled}
                isValidated={isSubmitted}
                isRepairSubmitted={repairReport?.submittedAt != null}
                isDeleteDisabled={repairReport != null || defectReport === null}
                canEditDefect={extraQualityPermissions.canEditReport}
                canInvalidateDefect={
                  extraQualityPermissions.canInvalidateReport
                }
                onSave={save}
                onInvalidate={handleInvalidate}
                onDelete={handleDelete}
              />
            </>
          ),
        },
      ]}
    />
  );
};
